import { createSelector } from 'reselect'
import { isEmpty, values, find, get, some } from 'lodash'
import moment from 'moment'

import { getCurrentMenuId } from './appState'
import { getSelectedMenusIds, getDeviceSubtype } from './config'

import DeviceSubtypes from '../utils/deviceSubtypes'
import { getNextDollar } from '../utils/charityConfig'
import { RICH_CHECKOUT_DEEPLINK_SCHEMA } from '../constants'
import { deepLinkSchemaURLPrefix } from '../utils/richCheckout'

export const getMenus = (state) => values(state.menus?.byId)

export const getStands = (state) => values(state.stands?.byId)

export const getIsMenusLoading = (state) => state?.menus?.isMenusLoading ?? false

export const getMenusById = (state) => state.menus?.byId || {}

export const getSelectedMenus = createSelector([getMenusById, getSelectedMenusIds], (menusById, selectedIds) =>
  selectedIds.map((id) => menusById[id]).filter((menu) => !!menu),
)

export const getCurrentMenu = createSelector(
  getMenusById,
  getCurrentMenuId,
  (menusById, currentMenuId) => menusById[currentMenuId] || {},
)

export const makeGetCurrentMenu = () => createSelector(getCurrentMenu, (menu) => menu)

// FS > QS Developers : Change this to get your test build to use FS mode.

export const getTabsModeEnabled = createSelector(getCurrentMenu, (menu) => menu.enableFullServiceInPos ?? false)

export const getKitchenPrintingEnabled = createSelector(getCurrentMenu, (menu) => menu?.enableQuickServiceKitchenPrinting)

export const getTextReceiptsEnabled = createSelector(getCurrentMenu, (menu) => menu?.enablePosSmsReceipts)

export const getPosQrReceiptsEnabled = createSelector(getCurrentMenu, (menu) => menu?.enablePosQrCodeReceipts)

export const getQrReceiptsUrl = (state) => {
  const menu = getCurrentMenu(state)
  const instanceName = process.env.VITE_INSTANCE_NAME ?? ''

  if (menu?.smsReceiptInstance) {
    return `https://${menu.smsReceiptInstance}.ordernext.com`
  }

  if (RICH_CHECKOUT_DEEPLINK_SCHEMA && RICH_CHECKOUT_DEEPLINK_SCHEMA !== 'undefined') {
    return deepLinkSchemaURLPrefix()
  }

  if (instanceName) {
    return `https://${instanceName}.ordernext.com`
  }

  return null
}

export const getKioskQrReceiptsEnabled = createSelector(getCurrentMenu, (menu) => menu?.enableKioskQrCodeReceipts)

export const getRoundUpForCharityFeatureEnabled = (state, total) => {
  const roundUpEnabled = getRoundUpForCharityEnabled(state)
  const charityOptionsEnabled = getCharityOptionsEnabled(state)
  const charityOptions = getCharityOptions(state)
  const isNextDollarZero = roundUpEnabled && getNextDollar(total) === 0

  if (!roundUpEnabled) {
    return false
  }

  if (isNextDollarZero && !charityOptionsEnabled) {
    return false
  }

  if (isNextDollarZero && charityOptionsEnabled && isEmpty(charityOptions)) {
    return false
  }

  return (charityOptionsEnabled || roundUpEnabled) ?? false
}

export const getCharityOptionsEnabled = createSelector(getCurrentMenu, (menu) => menu?.enableCharityOptions ?? false)
export const getRoundUpForCharityEnabled = createSelector(getCurrentMenu, (menu) => menu?.enableRoundUpForCharity ?? false)
export const getCharityOptions = createSelector(getCurrentMenu, (menu) => menu?.charityIncrements ?? [])
export const getCharityDescription = createSelector(getCurrentMenu, (menu) => menu?.charityItemSubtext ?? '')
export const getCharityName = createSelector(getCurrentMenu, (menu) => menu?.charityItemName ?? '')
export const getCharityImage = createSelector(getCurrentMenu, (menu) => menu?.charityImage ?? '')

export const getCashRoundingEnabled = createSelector(getCurrentMenu, (menu) => menu?.cashRoundingEnabled ?? false)
export const getCashRoundingMinimumCurrencyInCents = createSelector(
  getCurrentMenu,
  (menu) => menu?.cashRoundingMinimumCurrencyInCents ?? 5,
)

export const getRequireManagerPinForConfiguration = createSelector(getCurrentMenu, (menu) => menu?.requireManagerForPosConfig)

export const getEmailReceiptsEnabled = createSelector(getCurrentMenu, (menu) => menu?.enablePosEmailReceipts)

export const getRequirePaymentForTabs = createSelector(getCurrentMenu, (menu) => menu?.requirePaymentForPosTabs)

export const getIsCurrentMenuBellyUp = createSelector(
  getCurrentMenu,
  (menu) => menu?.configurationModelName?.toLowerCase().includes('belly-up') ?? false,
)

export const getIsBellyUpMenuAvailable = createSelector(getSelectedMenus, (menus) =>
  some(menus, (menu) => menu?.configurationModelName?.toLowerCase().includes('belly-up') ?? false),
)

export const getIsKiosk = createSelector(getDeviceSubtype, (deviceSubtype) => {
  return DeviceSubtypes.isKiosk(deviceSubtype)
})

export const getTipsEnabled = createSelector(getCurrentMenu, getIsKiosk, (menu, isKiosk) => {
  return !isEmpty(menu.tipSuggestions)
})

export const getCurrentStand = createSelector(getStands, getCurrentMenu, (stands, currentMenu) =>
  find(stands, ['uuid', currentMenu.standUuid]),
)

export const getCurrentMenuStand = createSelector(
  [(state) => state.stands?.byId, getCurrentMenu],
  (stands, menu) => stands[menu.standUuid] || {},
)

export const getIsStandAvailable = createSelector(getSelectedMenus, (menus) => {
  return some(menus, (menus) => menus?.isAvailable && menus?.orderNow)
})

export const getSelectedMenusImages = createSelector(getSelectedMenus, (selectedMenus) =>
  selectedMenus.map((menu) => get(menu, 'images.detail')).filter((image) => !!image),
)

export const getImagesForSelectedMenus = createSelector(getSelectedMenus, (menus) => {
  return menus.map((menu) => ({
    menuUuid: menu.uuid,
    name: menu.name,
    detail: menu.images.detail || null,
    list: menu.images.list || null,
    stand: menu.standImages.detail || null,
    standList: menu.standImages.list || null,
    charityImage: menu.charityImage || null,
  }))
})

export const getMenusRequestThreshold = () => 1

export const getMenusTimestamp = (state) => state?.menus?.timeStamp ?? moment().subtract(2, 'hours').toString()

export const getMenusNeedReload = createSelector(
  getMenus,
  getMenusTimestamp,
  getMenusRequestThreshold,
  (menus, timeStamp, threshold) => {
    const thresholdExceeded = moment().diff(moment(timeStamp), 'hours') > threshold
    const noRecordsAvailable = menus.length === 0

    if (thresholdExceeded || noRecordsAvailable) {
      return true
    } else {
      return false
    }
  },
)

export const getUseNormalTaxRounding = createSelector(getCurrentStand, (stand) => {
  return stand?.useNormalTaxRounding ?? false
})

export const getUseTaxClasses = createSelector(getCurrentStand, (stand) => {
  return stand?.useTaxClasses ?? false
})
